import { useCallback, useEffect, useState } from 'react';
import type { ConfiguratorProgress, VehicleRef } from './domain';
import { resumePoint, useConfiguratorState } from './store';

const KEY = 'horizon.configurator.motornet.progress';
const SCHEMA_VERSION = 2;

export type SavedProgress = {
	version: number;
	id: string;
	progress: ConfiguratorProgress;
	savedAt: number;
};

const STEPS = [
	'brand-selector',
	'model-selector',
	'customizer',
	'summary',
] as const;

export function readProgress(): SavedProgress | null {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as unknown;
		if (!isValid(parsed)) {
			window.localStorage.removeItem(KEY);
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

export function writeProgress(
	progress: ConfiguratorProgress,
	existingId?: string
): SavedProgress | null {
	if (typeof window === 'undefined') return null;
	const entry: SavedProgress = {
		version: SCHEMA_VERSION,
		id: existingId ?? makeShortId(),
		progress,
		savedAt: Date.now(),
	};
	try {
		window.localStorage.setItem(KEY, JSON.stringify(entry));
		return entry;
	} catch {
		return null;
	}
}

export function clearProgress(): void {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.removeItem(KEY);
	} catch {}
}

const isVehicleRef = (value: unknown): value is VehicleRef => {
	if (!value || typeof value !== 'object') return false;
	const v = value as Record<string, unknown>;
	return (
		typeof v.eurotaxCode === 'string' &&
		typeof v.vehicleKind === 'string' &&
		typeof v.year === 'number' &&
		typeof v.month === 'number'
	);
};

function isValid(value: unknown): value is SavedProgress {
	if (!value || typeof value !== 'object') return false;
	const v = value as Record<string, unknown>;
	if (v.version !== SCHEMA_VERSION) return false;
	if (typeof v.id !== 'string' || typeof v.savedAt !== 'number') return false;
	const p = v.progress as Record<string, unknown> | undefined;
	if (!p || typeof p !== 'object') return false;
	if (!STEPS.includes(p.step as typeof STEPS[number])) return false;
	if (p.makeCode !== null && typeof p.makeCode !== 'string') return false;
	if (p.makeLabel !== null && typeof p.makeLabel !== 'string') return false;
	if (p.modelCode !== null && typeof p.modelCode !== 'string') return false;
	if (p.modelLabel !== null && typeof p.modelLabel !== 'string') return false;
	if (p.vehicle !== null && !isVehicleRef(p.vehicle)) return false;
	return true;
}

const ALPHABET =
	'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';

function makeShortId(): string {
	const bytes = new Uint8Array(8);
	window.crypto.getRandomValues(bytes);
	return Array.from(bytes)
		.map((b) => ALPHABET[b & 63])
		.join('');
}

const DEBOUNCE_MS = 300;

export function useProgressPersistence() {
	useEffect(() => {
		let timer: ReturnType<typeof setTimeout> | null = null;

		const flush = (state: ConfiguratorProgress, hadMake: boolean) => {
			if (!state.makeCode) {
				if (hadMake) clearProgress();
				return;
			}
			writeProgress(state, readProgress()?.id);
		};

		const unsubscribe = useConfiguratorState.subscribe((state, prev) => {
			const hadMake = !!prev.makeCode;
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => {
				// la pronta consegna non è ripristinabile: si salva il passo da cui
				// la si raggiunge, non il riepilogo (vedi `resumePoint`)
				const at = resumePoint(state);
				flush(
					{
						step: at.step,
						makeCode: state.makeCode,
						makeLabel: state.makeLabel,
						modelCode: state.modelCode,
						modelLabel: state.modelLabel,
						vehicle: state.vehicle,
						subsection: at.subsection,
						tab: state.tab,
						selectedOptions: [...state.committedPicks],
						choices: [...state.choices],
					},
					hadMake
				);
			}, DEBOUNCE_MS);
		});

		return () => {
			if (timer) clearTimeout(timer);
			unsubscribe();
		};
	}, []);
}

type ResumePromptState =
	| { open: false }
	| {
			open: true;
			saved: SavedProgress;
			onContinue: () => void;
			onStartClean: () => void;
	  };

const URL_KEYS = ['step', 'makeCode', 'modelCode', 'eurotax'] as const;

function urlHasState(): boolean {
	if (typeof window === 'undefined') return false;
	const params = new URLSearchParams(window.location.search);
	return URL_KEYS.some((k) => params.has(k));
}

export function useResumePrompt(): ResumePromptState {
	const [saved] = useState<SavedProgress | null>(() => {
		if (urlHasState()) return null;
		const entry = readProgress();
		return entry?.progress.makeCode ? entry : null;
	});
	const [open, setOpen] = useState(() => !!saved);

	const onContinue = useCallback(() => {
		if (!saved) return;
		useConfiguratorState.getState().hydrate({
			step: saved.progress.step,
			makeCode: saved.progress.makeCode,
			makeLabel: saved.progress.makeLabel,
			modelCode: saved.progress.modelCode,
			modelLabel: saved.progress.modelLabel,
			vehicle: saved.progress.vehicle,
			subsection: saved.progress.subsection,
			tab: saved.progress.tab,
			optionIds: saved.progress.selectedOptions,
			choices: saved.progress.choices ?? [],
		});
		setOpen(false);
	}, [saved]);

	const onStartClean = useCallback(() => {
		clearProgress();
		setOpen(false);
	}, []);

	if (!open || !saved) return { open: false };

	return { open: true, saved, onContinue, onStartClean };
}
